import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';
import type { SelectOption } from './select.js';

/// O que a pessoa escolheu: uma das opções da lista, ou um texto que ela mesma digitou.
export type ComboboxChoice = { kind: 'option'; value: string } | { kind: 'text'; text: string };

/// Lista longa demais para um `Select`, e nem sempre fechada.
///
/// "Pago por" é o caso: são os sócios cadastrados, a empresa — e o terceiro, que não tem cadastro
/// nenhum. Rolar uma lista de vinte nomes atrás do certo é ruim; e obrigar a virar sócio quem
/// pagou uma vez é pior. Aqui digitar filtra a lista, e o que foi digitado também vale como busca
/// pelo nome quando `allowFreeText` está ligado.
///
/// Com `onQueryChange` a lista vem de fora: quem usa busca na API o que foi digitado e devolve as
/// `options` já filtradas — é o caso do cliente, que não tem teto e nunca pode vir inteiro.
export function Combobox({
  label,
  placeholder,
  selectedLabel,
  options,
  onSelect,
  allowFreeText = false,
  freeTextLabel = (text) => `Buscar por “${text}”`,
  emptyLabel = 'Nada encontrado',
  onQueryChange,
  onSubmitText,
  loading = false,
  status,
  onClear,
  disabled,
  error,
  hint,
  hideLabel = false,
  clearable = true,
  className,
  wrapDescriptions = false,
}: {
  label: string;
  placeholder: string;
  /// O que aparece no campo quando ninguém está digitando. Vazio mostra o `placeholder`.
  selectedLabel: string;
  options: SelectOption[];
  onSelect: (choice: ComboboxChoice) => void;
  allowFreeText?: boolean | undefined;
  freeTextLabel?: ((text: string) => string) | undefined;
  emptyLabel?: string | undefined;
  /// Busca remota: recebe o texto digitado (vazio ao desistir) e desliga o filtro local.
  onQueryChange?: ((text: string) => void) | undefined;
  /// Enter com texto digitado, antes de escolher a linha em destaque. É o caminho do leitor de
  /// código de barras: ele digita o código e o Enter mais rápido que a busca responde, e a linha em
  /// destaque ainda seria da busca anterior. Devolver `true` conclui e limpa o campo; `false`
  /// mantém o texto para a pessoa continuar. Setas antes do Enter voltam a escolher a linha.
  onSubmitText?: ((text: string) => Promise<boolean> | boolean) | undefined;
  loading?: boolean | undefined;
  /// Linha de situação no topo da lista, como "20 de 134 clientes".
  status?: string | undefined;
  /// O que o botão de limpar faz. Sem isto, limpar escolhe a primeira opção (o "todos" da lista).
  onClear?: (() => void) | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  /// O rótulo fica só para o leitor de tela, quando a tela já diz o que é o campo.
  hideLabel?: boolean | undefined;
  /// Sem o botão de limpar: a escolha é obrigatória, como a versão que se está vendo.
  clearable?: boolean | undefined;
  className?: string | undefined;
  /// Explicações de escolha ficam inteiras, inclusive no celular. O padrão compacto é preservado.
  wrapDescriptions?: boolean | undefined;
}) {
  const id = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  /// `null` = ninguém está digitando, então o campo mostra a escolha atual. Guardar o texto
  /// digitado à parte é o que permite desistir no Esc e voltar ao que estava.
  const [query, setQuery] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  /// A pessoa andou pela lista com as setas: aí o Enter escolhe a linha, não o texto.
  const [navigated, setNavigated] = useState(false);

  const typed = query ?? '';
  const remote = onQueryChange !== undefined;
  const matches = useMemo(
    () =>
      remote || typed.trim() === '' ? options : options.filter((o) => contains(o.label, typed)),
    [options, remote, typed],
  );
  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  /// A linha de texto livre só aparece quando o que foi digitado não é o nome exato de uma opção:
  /// oferecer "Buscar por Simone" logo abaixo da sócia Simone só faria duvidar de qual clicar.
  const freeText =
    allowFreeText &&
    typed.trim() !== '' &&
    !options.some((option) => option.label.toLowerCase() === typed.trim().toLowerCase());
  const rows: ComboboxChoice[] = [
    ...matches.map((option) => ({ kind: 'option' as const, value: option.value })),
    ...(freeText ? [{ kind: 'text' as const, text: typed.trim() }] : []),
  ];

  const dismiss = useCallback(() => {
    setOpen(false);
    // Desistir da busca devolve a lista remota ao começo, para a próxima abertura não herdar o filtro.
    if (query !== null) onQueryChange?.('');
    setQuery(null);
    setHighlight(0);
    setNavigated(false);
  }, [onQueryChange, query]);

  function choose(choice: ComboboxChoice) {
    onSelect(choice);
    dismiss();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return dismiss();
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setNavigated(true);
      if (rows.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      return setHighlight((current) => (current + step + rows.length) % rows.length);
    }
    if (event.key === 'Enter' && onSubmitText && typed.trim() !== '' && !navigated) {
      event.preventDefault();
      const text = typed.trim();
      void Promise.resolve(onSubmitText(text)).then((handled) => {
        if (handled) dismiss();
      });
      return undefined;
    }
    if (event.key === 'Enter') {
      const choice = rows[highlight];
      if (!choice) return;
      event.preventDefault();
      choose(choice);
    }
    return undefined;
  }

  return (
    <div className={cn('relative min-w-0 space-y-2', className)}>
      <label
        htmlFor={`${id}-input`}
        className={cn('block text-xs font-semibold text-content/70', hideLabel && 'sr-only')}
      >
        {label}
      </label>
      <div className="relative min-w-0" ref={fieldRef}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-content-faint" />
        <input
          id={`${id}-input`}
          ref={inputRef}
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={`${id}-options`}
          aria-autocomplete="list"
          {...(open && rows[highlight]
            ? { 'aria-activedescendant': `${id}-row-${highlight}` }
            : {})}
          value={query ?? selectedLabel}
          placeholder={placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            onQueryChange?.(event.target.value);
            setHighlight(0);
            setNavigated(false);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={descriptionId}
          className={cn(
            'hue-input h-12 w-full min-w-0 rounded-xl border bg-surface-elevated pl-10 pr-10 text-sm text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-danger/70' : 'border-surface-border',
          )}
        />
        {clearable && selectedLabel && query === null && !disabled ? (
          <button
            type="button"
            aria-label={`Limpar ${label.toLowerCase()}`}
            onClick={() => {
              // Limpar é escolher a primeira opção, que por convenção é o "todos" da lista.
              const first = options[0];
              if (onClear) {
                onClear();
                dismiss();
              } else if (first) choose({ kind: 'option', value: first.value });
              inputRef.current?.focus();
            }}
            className="absolute right-0.5 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : (
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-content-muted"
          />
        )}

        {open && !disabled ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={dismiss}
            width="anchor"
            className="max-h-64 rounded-2xl border border-surface-border bg-surface-card p-2 shadow-card"
          >
            <ul id={`${id}-options`} role="listbox" aria-label={label} aria-busy={loading}>
              {status ? (
                <li role="presentation" className="px-3 pb-1 pt-2 text-[11px] text-content-muted">
                  {status}
                </li>
              ) : null}
              {loading ? (
                <li role="presentation" className="px-3 py-3 text-xs text-content-muted">
                  Buscando…
                </li>
              ) : rows.length === 0 ? (
                <li className="px-3 py-3 text-xs text-content-faint">{emptyLabel}</li>
              ) : null}
              {rows.map((row, index) => {
                const option = row.kind === 'option' ? matches[index] : undefined;
                const chosen = option !== undefined && option.label === selectedLabel;
                return (
                  <li key={row.kind === 'option' ? row.value : 'free-text'}>
                    <button
                      type="button"
                      id={`${id}-row-${index}`}
                      role="option"
                      aria-selected={chosen}
                      aria-label={wrapDescriptions ? option?.label : undefined}
                      aria-describedby={
                        wrapDescriptions && option?.description
                          ? `${id}-description-${index}`
                          : undefined
                      }
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => choose(row)}
                      className={cn(
                        'flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition focus-visible:outline-none',
                        index === highlight && 'bg-surface-elevated',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block text-xs font-medium',
                            wrapDescriptions ? 'whitespace-normal break-words' : 'truncate',
                          )}
                        >
                          {option ? option.label : freeTextLabel(typed.trim())}
                        </span>
                        {option?.description ? (
                          <span
                            id={`${id}-description-${index}`}
                            className={cn(
                              'block text-[11px] text-content-muted',
                              wrapDescriptions
                                ? 'whitespace-normal break-words leading-5'
                                : 'truncate',
                            )}
                          >
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                      {chosen ? <Check className="size-4 shrink-0 text-brand-400" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </FloatingPanel>
        ) : null}
      </div>
      {error ? (
        <p id={descriptionId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/// Comparação sem acento e sem caixa: quem digita "socio" tem de achar "Sócio".
function contains(haystack: string, needle: string): boolean {
  return fold(haystack).includes(fold(needle.trim()));
}

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
